import React from "react";
import { createTheme } from "@mui/material";
import * as Colors from "@mui/material/colors";
import { getContrastRatio, alpha } from "@mui/material/styles";
import { contrastColor } from "utils/contrast-color";
import { refreshDesignsDev } from "shared/env";
import { DropdownChevron } from "component/shared/dropdown-chevron";

// Design tokens from mobile-admin theme (Q1 2026)
// Keeping consistent colors across web and mobile
export const baseColors = {
	white: "#FFFFFF",

	orange25: "#FEEFE8",
	orange: "#FC7434",
	orange100: "#FEDBCA",
	orange200: "#FDB898",
	orange300: "#FC9666",
	orange500: "#F95203",
	orange600: "#C74102",
	orange700: "#943102",

	teal25: "#DFF3F6",
	teal50: "#A4D1D7",
	teal75: "#93C8CF",
	teal100: "#81C0C8",
	teal200: "#5EAFB8",
	teal300: "#45959E",
	teal: "#36747B",
	teal400: "#35747b",
	teal500: "#265257",
	teal600: "#163134",
	teal700: "#070F10",
	
	gray50: "#FAFAFA",
	gray75: "#F0F0F0",
	gray100: "#EBEBEB",
	gray200: "#B9B9B9",
	gray300: "#A6A6A6",
	gray: "#939393",
	gray500: "#808080",
	gray600: "#6C6C6C",
	gray700: "#595959",
	

	grayMedium: "#DDE7E8",
	slateTeal: "#506164",
	palePeach: "#FFEFD6",
	softYellow: "#FFDA90",
	goldenOrange: "#FDA61A",
	vibrantOrange: "#FA6800",
	brightRed: "#FF4444",
	deepTeal: "#06393F",
	softCyan: "#8ECED5",
	lightAqua: "#C9F4F9",
	softLavender: "#DBD9F0",
	palePurple: "#9C94D6",
	royalPurple: "#6357BC",

	red500: "#FF4444",
	gold500: "#FDA61A",
	deepPurple: "#6B46C1",

	grayLight: "#F8F8F8",
	// Q2 2026 input design-system field-state tokens (MAX-3703).
	fieldBorderHover: "#FFDCC8", // pale peach — hover outline
	fieldBorderWarning: "#F59E0B", // amber — warning outline
	grayBatman: "#707070",
	graySectionHeader: "#F2F2F2",
	darkGray: "#707070",
	darkBlack: "#1D1E1E",
	mutedGray: "#5F6368",

	black: "#000000",
	darkCharcoal: "#1C1B1F",
	disabled: "rgba(168, 176, 177, 0.7)",
};

export const designTokens = {
	colors: {
		// Primary brand colors
		brandOrange: "#E85F2A",
		brandTeal: baseColors.teal,

		// Q2 2026 tag/pill design-system tokens (MAX-3685)
		borderCard: baseColors.grayMedium, // #DDE7E8 — Figma "Border/border-card" (table outline)
		tableHeaderSurface: baseColors.grayLight, // #F8F8F8 — table header row fill
		tagPrimarySurface: "#C9E4E7", // teal pill background (e.g. Admin role)
		tagPrimaryText: baseColors.teal600, // #163134
		tagBrandSurface: "#FFF3EC", // peach pill background (e.g. Agent role)
		tagBrandText: "#B84319",
		tagNeutralSurface: baseColors.gray100, // #EBEBEB — routing pills
		tagNeutralText: baseColors.darkCharcoal, // #1C1B1F
		tagSuccessSurface: "#C6F5D7", // Figma "Surface/tag-success"
		tagSuccessText: "#136E35", // Figma "Text/tag-success"
		tagWarningSurface: "#FFF9E0", // Figma "Surface/tag-warning"
		tagWarningText: "#9D6506", // Figma "Text/tag-warning"
		tagErrorSurface: "#FFE0E0", // Figma "Surface/tag-error"
		tagErrorText: "#992B2B", // Figma "Text/tag-error"
		tagInfoSurface: "#DFF4F7", // Figma "Surface/tag-info"
		tagInfoText: "#506264", // Figma "Text/tag-info"
		tagOutlineSurface: baseColors.white, // Figma "Surface/tag-outline"
		tagOutlineText: "#707070", // Figma "Text/tag-outline"
		tagOutlineBorder: "#707070", // Figma "Border/tag-outline"
		tagOrangeSurface: baseColors.orange100, // #FEDBCA — Beta tag
		tagOrangeText: baseColors.darkCharcoal, // #1C1B1F

		// Base colors
		white: baseColors.white,
		black: baseColors.black,

		// UI colors
		grayLight: baseColors.grayLight,
		grayDark: baseColors.mutedGray,
		divider: baseColors.gray100,

		// Interactive elements
		iconInteractive: baseColors.orange,
		iconNeutral: baseColors.mutedGray,
		linkPrimary: baseColors.teal,

		// Semantic colors
		successLight: "#C6F5D7",
		success: "#22C55E",
		successDark: "#126E35",
		errorLight: "#FFE0E0",
		error: baseColors.brightRed,
		errorDark: "#BF3636",
		warningLight: "#FFF9E0",
		warning: "#F7B13B",
		warningDark: "#D38709",
		infoLight: baseColors.palePeach,
		info: baseColors.softYellow,
	},
	typography: {
		fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif",
	},
	spacing: {
		s4: 4,
		s8: 8,
		s12: 12,
		s16: 16,
		s20: 20,
		s24: 24,
		s32: 32,
		s40: 40,
		s48: 48,
		s56: 56,
		s64: 64,
		s80: 80,
		s120: 120,
	},
};

// Extend Material-UI palette with custom colors from design tokens
interface CustomPalette {
	primary: string;
	border: string;
	navBackground: string;
	navText: string;
	textPrimary: string;
	textSecondary: string;
	textTertiary: string;
	tertiaryAction: string;
	dialogBackground: string;
	sidebarBackground: string;
	divider: string;
	// Semantic colors
	successLight: string;
	success: string;
	successDark: string;
	errorLight: string;
	errorDark: string;
	warningLight: string;
	warning: string;
	warningDark: string;
	infoLight: string;
	info: string;
	// Tag/pill + card-border tokens
	borderCard: string;
	tableHeaderSurface: string;
	tagPrimarySurface: string;
	tagPrimaryText: string;
	tagBrandSurface: string;
	tagBrandText: string;
	tagNeutralSurface: string;
	tagNeutralText: string;
	tagSuccessSurface: string;
	tagSuccessText: string;
	tagWarningSurface: string;
	tagWarningText: string;
	tagErrorSurface: string;
	tagErrorText: string;
	tagInfoSurface: string;
	tagInfoText: string;
	tagOutlineSurface: string;
	tagOutlineText: string;
	tagOutlineBorder: string;
	tagOrangeSurface: string;
	tagOrangeText: string;
}

type CustomPaletteOptions = Partial<CustomPalette>;

// v5 palette augmentation — theme.ts now builds the theme with @mui/material's createTheme,
// so the custom tokens + PaletteColor.faded must be declared on the v5 palette types.
declare module "@mui/material/styles/createPalette" {
	interface PaletteColor {
		faded: string;
	}
	interface SimplePaletteColorOptions {
		faded?: string;
	}
	interface Palette {
		custom: CustomPalette;
	}
	interface PaletteOptions {
		custom?: CustomPaletteOptions;
	}
}

export const getTheme = (primaryColor?: string, secondaryColor?: string, navBackgroundColor?: string) => {
	// Use design tokens as defaults when flag is on, otherwise use legacy colors
	if (!primaryColor) {
		primaryColor = designTokens.colors.brandTeal;
	}
	if (!secondaryColor) {
		secondaryColor = designTokens.colors.brandOrange;
	}
	if (!navBackgroundColor) {
		navBackgroundColor = designTokens.colors.grayLight;
	}
	const navTextColor = contrastColor(navBackgroundColor);
	const theme = createTheme({
		palette: {
			primary: {
				main: primaryColor,
				faded: alpha(primaryColor, 0.3),
			},
			secondary: {
				main: secondaryColor,
			},
			text: {
				primary: baseColors.teal600,
				secondary: designTokens.colors.grayDark,
			},
			error: {
				main: "#ef4444",
			},
			background: {
				default: designTokens.colors.white,
				paper: designTokens.colors.white,
			},
			custom: {
				primary: designTokens.colors.brandOrange,
				border: designTokens.colors.divider,
				textPrimary: baseColors.darkCharcoal,
				textSecondary: designTokens.colors.grayDark,
				textTertiary: baseColors.gray600, // #6C6C6C — Figma --text/tab-inactive
				tertiaryAction: baseColors.teal, // #36747B — Figma --text/tertiary-action-default
				dialogBackground: "#3d4251",
				navBackground: navBackgroundColor,
				navText: navTextColor,
				sidebarBackground: navBackgroundColor,
				divider: designTokens.colors.divider,
				// Semantic colors
				successLight: designTokens.colors.successLight,
				success: designTokens.colors.success,
				successDark: designTokens.colors.successDark,
				errorLight: designTokens.colors.errorLight,
				errorDark: designTokens.colors.errorDark,
				warningLight: designTokens.colors.warningLight,
				warning: designTokens.colors.warning,
				warningDark: designTokens.colors.warningDark,
				infoLight: designTokens.colors.infoLight,
				info: designTokens.colors.info,
				// Tag/pill + card-border tokens (MAX-3685)
				borderCard: designTokens.colors.borderCard,
				tableHeaderSurface: designTokens.colors.tableHeaderSurface,
				tagPrimarySurface: designTokens.colors.tagPrimarySurface,
				tagPrimaryText: designTokens.colors.tagPrimaryText,
				tagBrandSurface: designTokens.colors.tagBrandSurface,
				tagBrandText: designTokens.colors.tagBrandText,
				tagNeutralSurface: designTokens.colors.tagNeutralSurface,
				tagNeutralText: designTokens.colors.tagNeutralText,
				tagSuccessSurface: designTokens.colors.tagSuccessSurface,
				tagSuccessText: designTokens.colors.tagSuccessText,
				tagWarningSurface: designTokens.colors.tagWarningSurface,
				tagWarningText: designTokens.colors.tagWarningText,
				tagErrorSurface: designTokens.colors.tagErrorSurface,
				tagErrorText: designTokens.colors.tagErrorText,
				tagInfoSurface: designTokens.colors.tagInfoSurface,
				tagInfoText: designTokens.colors.tagInfoText,
				tagOutlineSurface: designTokens.colors.tagOutlineSurface,
				tagOutlineText: designTokens.colors.tagOutlineText,
				tagOutlineBorder: designTokens.colors.tagOutlineBorder,
				tagOrangeSurface: designTokens.colors.tagOrangeSurface,
				tagOrangeText: designTokens.colors.tagOrangeText,
			},
		},
		typography: {
			fontFamily: designTokens.typography.fontFamily,
			h1: {
				fontSize: "2rem",
				...{ color: designTokens.colors.black, fontWeight: 500 },
			},
			h2: {
				fontSize: "1.75rem",
			},
			h3: {
				fontSize: "1.5rem",
				...{ color: designTokens.colors.grayDark, fontWeight: 600 },
			},
			h4: {
				fontSize: "1.15rem",
			},
			h5: {
				fontSize: "1rem",
			},
			h6: {
				fontSize: "1rem",
			},
			body1: {
				fontSize: "1rem",
			},
			body2: {
				fontSize: "1rem",
			},
			subtitle1: {
				fontSize: "0.625rem",
			},
		},
		components: {
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						backgroundColor: baseColors.white,
						color: baseColors.black,
						"& h2": {
							fontSize: "1.5rem",
							fontWeight: 700,
							lineHeight: "2rem",
							letterSpacing: 0,
						},
					},
				},
			},
			MuiDialogContent: {
				styleOverrides: {
					root: {
						padding: "24px 24px",
					},
				},
			},
			MuiLink: {
				styleOverrides: {
					root: {
						color: designTokens.colors.linkPrimary,
						textDecoration: "none !important",
					},
				},
			},
			MuiCardContent: {
				styleOverrides: {
					root: {
						"&:last-child": {
							paddingBottom: undefined,
						},
					},
				},
			},
			MuiCardActions: {
				styleOverrides: {
					root: {
						padding: 16,
						paddingTop: 0,
					},
				},
			},
			// v5: ExpansionPanel renamed to Accordion; the old MuiExpansionPanelActions key
			// is silently ignored, so this must be MuiAccordionActions.
			MuiAccordionActions: {
				styleOverrides: {
					root: {
						padding: 16,
						paddingTop: 0,
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						fontSize: "1rem",
						"@media (min-width: 960px)": {
							fontSize: "1rem",
						},
						textTransform: "none" as const,
					},
				},
			},
			MuiButton: {
				// Q2 2026 design system (MAX-3704), gated: the standard button is "Medium" —
				// 16px Semibold label, 20px line-height, 8px×16px padding (→ 36px tall).
				// Flag-off keeps the prior medium-weight / default-padding button.
				styleOverrides: {
					root: {
						fontSize: "1rem",
						borderRadius: 8,
						textTransform: "none" as const,
						fontWeight: refreshDesignsDev ? 600 : 500,
						...(refreshDesignsDev ? { padding: "8px 16px", lineHeight: "20px" } : {}),
					},
					// v5 cascade fix (MAX-4133): styleOverrides.root is injected after the
					// component's built-in variant/size styles, so the root fontSize/padding
					// above started clobbering the text-button metrics (in v4/JSS the
					// text/textSizeSmall rules came later in the sheet and won). Restore the
					// MUI defaults so text links (e.g. card "View Details") keep the
					// pre-upgrade compact rendering.
					text: {
						padding: "6px 8px",
					},
					textSizeSmall: {
						padding: "4px 5px",
						fontSize: "0.8125rem",
					},
					contained: {
						boxShadow: "none",
						"&:hover": {
							boxShadow: "none",
						},
						"&:active": {
							boxShadow: "none",
						},
					},
					containedPrimary: {
						backgroundColor: primaryColor,
						color: "#fff",
						"&:hover": {
							backgroundColor: baseColors.teal500,
						},
					},
					containedSecondary: {
						backgroundColor: secondaryColor,
						color: "#fff",
						"&:hover": {
							backgroundColor: baseColors.orange500,
						},
					},
					outlined: {
						borderWidth: 1,
						"&:hover": {
							borderWidth: 1,
						},
					},
					outlinedPrimary: {
						borderColor: primaryColor,
						color: primaryColor,
						"&:hover": {
							borderColor: baseColors.teal500,
							backgroundColor: "rgba(54, 116, 123, 0.04)",
						},
					},
					outlinedSecondary: {
						borderColor: secondaryColor,
						color: secondaryColor,
						"&:hover": {
							borderColor: baseColors.orange500,
							backgroundColor: "rgba(252, 116, 52, 0.04)",
						},
					},
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						color: baseColors.gray200,
						"&:hover": {
							color: secondaryColor,
							backgroundColor: "transparent",
						},
					},
					colorSecondary: {
						color: baseColors.gray200,
						"&:hover": {
							color: secondaryColor,
							backgroundColor: alpha(secondaryColor, 0.04),
						},
						// v5: JSS local-rule ref "&$checked" → global class ".Mui-checked".
						"&.Mui-checked": {
							color: secondaryColor,
							"&:hover": {
								backgroundColor: alpha(secondaryColor, 0.04),
							},
						},
						"&.Mui-disabled": {
							color: baseColors.gray200,
						},
					},
					colorPrimary: {
						color: baseColors.gray200,
						"&:hover": {
							color: primaryColor,
							backgroundColor: alpha(primaryColor, 0.04),
						},
						"&.Mui-checked": {
							color: primaryColor,
							"&:hover": {
								backgroundColor: alpha(primaryColor, 0.04),
							},
						},
						"&.Mui-disabled": {
							color: baseColors.gray200,
						},
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						backgroundColor: baseColors.darkCharcoal,
						color: baseColors.white,
						fontSize: "0.875rem",
						borderRadius: 4,
						padding: "8px 12px",
					},
					arrow: {
						color: baseColors.darkCharcoal,
					},
				},
			},
			MuiOutlinedInput: {
				// Q2 2026 input design system (MAX-3703), gated behind refreshDesignsDev:
				// 4px radius, #f8f8f8 hover/focus/disabled surface, pale-peach hover outline,
				// brand-orange 1px focus outline. Flag-off keeps the prior styling untouched.
				styleOverrides: {
					root: {
						borderRadius: refreshDesignsDev ? 4 : 8,
						backgroundColor: baseColors.white,
						"&:hover": {
							backgroundColor: refreshDesignsDev ? baseColors.grayLight : baseColors.gray50,
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: refreshDesignsDev ? baseColors.fieldBorderHover : alpha(secondaryColor, 0.4),
						},
						"&.Mui-focused": {
							backgroundColor: refreshDesignsDev ? baseColors.grayLight : baseColors.gray50,
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: secondaryColor,
							borderWidth: refreshDesignsDev ? 1 : 2,
						},
						"&.Mui-disabled": {
							backgroundColor: refreshDesignsDev ? baseColors.grayLight : baseColors.gray75,
						},
						// Dropdown caret greys out when the field is disabled (design spec).
						...(refreshDesignsDev
							? {
									"&.Mui-disabled .MuiSelect-icon, &.Mui-disabled .MuiAutocomplete-popupIndicator": {
										color: baseColors.gray,
									},
							  }
							: {}),
						"& .MuiInputAdornment-positionEnd": {
							color: secondaryColor,
						},
					},
					notchedOutline: {
						borderColor: designTokens.colors.divider,
					},
				},
			},
			// Q2 2026 (MAX-3703), gated: make the design-system chevron the default caret for
			// every dropdown app-wide — MUI Select (e.g. FullWidthDropdown) and Autocomplete —
			// replacing MUI's default filled ArrowDropDown triangle. In v5 the default-prop
			// override moves from theme.props to components.<Name>.defaultProps. Flag-off keeps
			// MUI defaults.
			MuiSelect: {
				...(refreshDesignsDev ? { defaultProps: { IconComponent: DropdownChevron } } : {}),
				styleOverrides: {
					icon: {
						color: secondaryColor,
					},
				},
			},
			MuiNativeSelect: {
				...(refreshDesignsDev ? { defaultProps: { IconComponent: DropdownChevron } } : {}),
			},
			// Q2 2026 dropdown spec (MAX-3703), gated: 16px L/R + 12px T/B padding, caret
			// inset 16px from the right edge, brand-orange caret. Field-state colors/radius
			// come from MuiOutlinedInput above. Flag-off leaves Autocomplete unstyled here.
			// Autocomplete is a core component in v5, so its defaultProps/styleOverrides are
			// natively typed (no lab class-key augmentation needed).
			MuiAutocomplete: {
				...(refreshDesignsDev
					? {
							defaultProps: { popupIcon: React.createElement(DropdownChevron) },
							styleOverrides: {
								inputRoot: {
									'&[class*="MuiOutlinedInput-root"]': {
										padding: "12px 48px 12px 16px",
										"& .MuiAutocomplete-input": { padding: 0 },
										"& .MuiAutocomplete-endAdornment": { right: 16 },
									},
								},
								popupIndicator: {
									color: secondaryColor,
								},
							},
					  }
					: {}),
			},
			MuiInputBase: {
				styleOverrides: {
					root: {
						fontSize: "1rem",
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						fontSize: "1rem",
						color: designTokens.colors.grayDark,
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						borderRadius: 16,
						fontSize: "0.875rem",
						height: 32,
					},
					colorPrimary: {
						backgroundColor: baseColors.orange100,
						color: baseColors.orange700,
					},
					colorSecondary: {
						backgroundColor: baseColors.teal100,
						color: baseColors.teal700,
					},
					outlined: {
						borderColor: designTokens.colors.divider,
					},
				},
			},
			MuiSwitch: {
				styleOverrides: {
					root: {
						width: 46,
						height: 26,
						padding: 0,
						margin: 8,
					},
					switchBase: {
						padding: 3,
						// v5: "&$checked" → ".Mui-checked"; "& + $track" → "& + .MuiSwitch-track".
						"&.Mui-checked": {
							transform: "translateX(20px)",
							color: baseColors.white,
							"& + .MuiSwitch-track": {
								backgroundColor: designTokens.colors.brandOrange,
								opacity: 1,
							},
						},
					},
					thumb: {
						width: 20,
						height: 20,
						boxShadow: "0 0 0 1.5px rgba(0,0,0,0.15)",
						backgroundColor: baseColors.white,
					},
					track: {
						borderRadius: 13,
						backgroundColor: baseColors.gray300,
						opacity: 1,
					},
				},
			},
			MuiSnackbar: {
				styleOverrides: {
					anchorOriginBottomLeft: {
						left: "auto",
						right: 24,
						"@media (min-width:600px)": {
							left: "auto",
							right: 24,
						},
					},
					anchorOriginBottomCenter: {
						left: "auto",
						right: 24,
						transform: "none",
						"@media (min-width:600px)": {
							left: "auto",
							right: 24,
							transform: "none",
						},
					},
					anchorOriginTopLeft: {
						left: "auto",
						right: 24,
						"@media (min-width:600px)": {
							left: "auto",
							right: 24,
						},
					},
					anchorOriginTopCenter: {
						left: "auto",
						right: 24,
						transform: "none",
						"@media (min-width:600px)": {
							left: "auto",
							right: 24,
							transform: "none",
						},
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					standardSuccess: {
						backgroundColor: designTokens.colors.successLight,
						color: designTokens.colors.black,
						"& .MuiAlert-icon": {
							color: designTokens.colors.success,
						},
					},
					filledSuccess: {
						backgroundColor: designTokens.colors.success,
						color: "#fff",
					},
				},
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						minWidth: 40,
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						borderBottom: `1px solid ${baseColors.gray100}`,
					},
					sizeSmall: {
						padding: 8,
					},
				},
			},
		},
	});
	return theme;
}